<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { addCursorLoader, currentYear, BreadcrumbContainer, Breadcrumbs, DmButton, Loader, removeCursorLoader, useToasterInSetup } from 'dmui_components'
import ReportSection from './ReportSection.vue'
import Report10kSections from './Report10kSections.vue'
import ReportRepository from '../repository/ReportRepository'
import useFormErrors from '@/components/useFormErrors'
import ReportDetails from './ReportDetails.vue'
import ReportInput from './ReportInput.vue'
import useSectionMgmt from '../composables/useSectionMgmt'

import permissionChecker from '@/components/permissionChecker'

const props = defineProps({
	reportId: {
		type: Number,
		default: undefined
	},
	documentType: {
		type: String,
		default: undefined
	},
	orgId: {
		type: Number,
		default: undefined
	},
	documentYear: {
		type: Number,
		default: undefined
	},
	permission: {
		type: Array,
		default: () => []
	},
	source: {
		type: String,
		default: 'Live'
	},
	expectedDate: {
		type: String,
		default: undefined
	}
})

const { hasPermission } = permissionChecker()
const readonly = computed(() => !hasPermission('reports', 'wd'))

const { setErrors, clearError, errors } = useFormErrors()
const { checkIfCSRD, getSectionTypes, createSectionDataTypeDict, createSectionDataArray, getSectionDiffs, getExclusiveCSRDTypes } = useSectionMgmt()

const DEFAULTS = {
	document_year: props.documentYear || currentYear,
	document_type: props.documentType,
	org: props.orgId ? { org_id: props.orgId } : undefined,
	published_at: props.expectedDate && props.expectedDate !== 'null' ? new Date(props.expectedDate).toISOString() : undefined
}
const report = ref({ ...DEFAULTS })
const fileData = ref({})

const sections = ref({})
const sectionErrors = ref({})
const validateSections = ref(false)
const loading = ref(false)

const toaster = useToasterInSetup({ page: 'EditReport' }, onUnmounted)
const router = useRouter()

const rootBreadcrumb = [{ title: `${props.source} reports`, icon: 'uil-arrow-left', path: '/document/report', query: { tabs: `${props.source.toLowerCase()}-reports` } }]

watch(
	() => props.reportId,
	async (newVal) => {
		if (!newVal) {
			report.value = { ...DEFAULTS }
		} else {
			try {
				addCursorLoader()
				report.value = await ReportRepository.getReportDetails(newVal, props.documentType)
				sections.value = createSectionDataTypeDict([...(report.value?.sections || [])], report.value?.is_csrd)
			} catch (error) {
				console.error('Error while fetching report details', error)
				toaster.show('Failed to fetch report details. Please try again.')
			} finally {
				removeCursorLoader()
			}
		}
	},
	{ immediate: true }
)

async function onSaveReport() {
	toaster.dismiss()
	validateSections.value = true
	if (Object.keys(sectionErrors.value)?.length) {
		toaster.showValidationErrorMessage()
		return
	}
	setErrors()
	try {
		loading.value = true
		addCursorLoader()
		if (!checkIfCSRD(report.value?.document_type)) {
			for (const section of getExclusiveCSRDTypes()) {
				delete sections.value[section]
			}
		}
		report.value.sections = createSectionDataArray(sections.value)
		if (props.reportId) {
			await ReportRepository.putReport({ report: report.value, section_diffs: getSectionDiffs(report.value.sections) })
		} else {
			await ReportRepository.postReport({ report: report.value, file: fileData.value?.file })
		}
		router.push(`/document/report?tabs=${props.source.toLowerCase()}-reports`)
	} catch (error) {
		removeCursorLoader()
		loading.value = false

		if (error.status === 422) {
			setErrors(error.detail)
			toaster.showValidationErrorMessage()
		} else {
			toaster.show('Failed in saving report')
			console.error('Save report failed', { error })
		}
	}
}

function onUpdateSectionError({ type, error }) {
	if (error) {
		sectionErrors.value[type] = error
	} else {
		delete sectionErrors.value[type]
	}
}

function onAddSection({ type, data }) {
	sections.value[type] = [...(sections.value[type] || []), data]
}

function onDeleteSection({ type, data }) {
	sections.value[type] = [...(sections.value[type]?.filter((section) => section !== data) || [])]
}

watch([report, fileData], () => toaster.dismiss(), { deep: true })
</script>

<template>
	<BreadcrumbContainer>
		<Breadcrumbs :data="[...rootBreadcrumb]" />
	</BreadcrumbContainer>
	<Loader v-if="reportId && !report?.report_id" />
	<div v-else class="form-content">
		<ReportDetails v-if="reportId && report?.report_id" v-model:report="report" :read-only="readonly || (report.status && report.status !== 'Done')">
			<div v-if="!readonly && report?.document_type !== '10k'" class="action-buttons">
				<DmButton id="save" :disabled="loading" @click.prevent="onSaveReport">Save</DmButton>
			</div>
		</ReportDetails>
		<ReportInput v-else v-model:report="report" v-model:file="fileData" :source="source.toLowerCase()" :errors="errors" @update:errors="clearError">
			<div :class="{ 'hide-save-btn': report?.document_type == '10k' }" class="action-buttons">
				<DmButton id="save" :disabled="loading" @click.prevent="onSaveReport">Save</DmButton>
			</div>
		</ReportInput>
		<template v-if="!reportId || Object.keys(sections).length > 0">
			<Report10kSections v-if="report.document_type === '10k'" :report-id="reportId" :section-types="report.sections" />
			<template v-else>
				<div v-for="(sectionText, sectionType) in getSectionTypes(report?.document_type)" :key="sectionType">
					<h3 class="primary-text bold">{{ sectionText }}</h3>
					<ReportSection
						:section-type="sectionType"
						:section-data="sections[sectionType]"
						:validate-section="validateSections"
						:report-id="reportId"
						:readonly="readonly || (report.status && report.status !== 'Done')"
						:validation-error="errors?.sections?.[0]?.message"
						@add:section-data="onAddSection"
						@delete:section-data="onDeleteSection"
						@update:errors="onUpdateSectionError" />
				</div>
			</template>
		</template>
	</div>
</template>
<style scoped lang="less">
.form-content {
	display: flex;
	flex-direction: column;
	gap: @medium;
	width: 550px;
}
:deep(.bordered-content) {
	border: 1px solid @gray-30;
	border-radius: 6px;
	padding: @midi;
	&.error {
		border: 2px solid @dm-wine;
	}
}

:deep(.header-content) {
	display: flex;
	align-items: center;
	gap: @micro;
	margin-top: @huge;
	.action-buttons {
		margin-left: auto;
		display: flex;
		gap: @micro;
	}
}
// TODO: remove once new breadcrumb is deployed
.large-screen-with-menu {
	left: auto !important;
}
.breadcrumb-container {
	padding: @micro;
	min-height: @micro;
}
.hide-save-btn {
	visibility: hidden;
}
</style>
